import styled from "@emotion/styled";
import { Percentage, Speed, SpeedContainer, SubSpeed, SubtitleSpan, SubtitleRightSpan, PercentageDiv } from "./resultElements";
import { ModemFinalDE } from "@visonum/network-quality-sdk";
import { getSpeedKind } from "../common/resultUtils";
import ProgressBar from "../common/ProgressBar";
import NavigationButton from "../../elements/NavigationButton";
import HeaderWithTooltip from "../../elements/HeaderWithTooltip";
import { CommonButtonDiv } from "../../elements/Buttons";
import * as R from "ramda";
import { NQRecordPrepareResult } from "../../../../features/history/types";
import { getDeviceText, getRouterSpeed, nullOrValue } from "../../../../helper/utils";
import t from "../../../../helper/t";
import { connectionToStr } from "./connectionToStr";

import InfoCircleSvg from "../../../../../assets/icons/splus3/download-info-circle.svg";

const RootDiv = styled.div(({ theme }) => ([
    {
        display: "flex",
        flexGrow: 10,
        padding: 20,
        columnGap: "3.6vw",

        [theme.splus3.breakpoints.smallDesktop]: {
            columnGap: "5vw",
        },

        [theme.splus3.breakpoints.mobile]: {
            flexDirection: "column",
            rowGap: 25,
            padding: 16,
        },
    }
]));

const LeftPartDiv = styled.div([
    {
        display: "flex",
        flexDirection: "column",
        flexGrow: 10,
    }
]);

const RightPartDiv = styled.div(({ theme }) => ([
    theme.splus3.background.primary,
    theme.splus3.shadow.primary,
    {
        display: "flex",
        columnGap: 16,
        minWidth: 170,
        maxWidth: 420,
        width: "30vw",
        borderRadius: 6,
        padding: 20,

        [theme.splus3.breakpoints.mobile]: {
            width: "auto",
            columnGap: 8,
            paddingLeft: 12,
            paddingRight: 12,
        },
    }
]));

const RightContentDiv = styled.div(({ theme }) => ([
    {
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
    }
]));

const RatingContainerDiv = styled.div(({ theme }) => ([
    {
        display: "flex",
        alignItems: "center",
    }
]));

const RatingText = styled.span(({ theme }) => ([
    theme.splus3.foreground.monochrome600,
    theme.splus3.typography.smallLite,
    {
        [theme.splus3.breakpoints.mobile]: [
            theme.splus3.typography.font12Lite,
        ],
    }
]));

const RightSpacer = styled.div(({ theme }) => ([
    {
        height: 32,

        [theme.splus3.breakpoints.mobile]: {
            height: 16,
        },
    }
]));

const AboutButtonDiv = styled(CommonButtonDiv)(({ theme }) => ([
    theme.splus3.typography.small,
    {
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 16,
        paddingRight: 16,
        minWidth: 0,

        [theme.splus3.breakpoints.mobile]: [
            {
                paddingTop: 4,
                paddingBottom: 4,
                paddingLeft: 10,
                paddingRight: 10,
            }
        ],
    },
]));

interface DownloadCardProps {
    prepareResult: NQRecordPrepareResult;
    modemFinal: ModemFinalDE;
    downloadSpeed: number;
    showCompareText?: boolean;
    onClick?: () => void;
}

const DownloadCard: React.FC<DownloadCardProps> = ({ prepareResult, modemFinal, downloadSpeed, showCompareText = true, onClick }) => {
    const bookedDownloadSpeedMax = nullOrValue(prepareResult.init.modem.bookedDownloadSpeedMax);
    const bookedDownloadSpeedAvg = nullOrValue(prepareResult.init.modem.bookedDownloadSpeedAvg);
    const bookedDownloadSpeedMin = nullOrValue(prepareResult.init.modem.bookedDownloadSpeedMin);
    const downloadModemSpeed = getRouterSpeed(modemFinal);
    const deviceName = getDeviceText(prepareResult, t("Messung an Deinem Gerät"));
    const routerName = nullOrValue(prepareResult.init.modem.name);

    const connectionSubtitle = () => {
        const connectionStr = connectionToStr(modemFinal.connectionType);
        return <>{deviceName} {connectionStr === null ? "" : `(${connectionStr})`}</>
    }

    const progress = (currentSpeedMbps: number) => {
        if (bookedDownloadSpeedMax === null ||
            bookedDownloadSpeedAvg === null ||
            bookedDownloadSpeedMin === null) {
            return null;
        }

        const max = bookedDownloadSpeedMax * 1e-6;
        const avg = bookedDownloadSpeedAvg * 1e-6;
        const min = bookedDownloadSpeedMin * 1e-6;
        const current = currentSpeedMbps * 1e-6;

        const speedKind = getSpeedKind(current, avg, min);

        return (
            <ProgressBar speedKind={speedKind} min={min} avg={avg} max={max} current={current} showCompareText={showCompareText} />
        );
    }

    const rate = (currentSpeedMbps: number) => {
        if (bookedDownloadSpeedMax === null ||
            bookedDownloadSpeedAvg === null ||
            bookedDownloadSpeedMin === null) {
            return null;
        }

        const text = (): string => {
            if (currentSpeedMbps > bookedDownloadSpeedAvg) {
                return t("Gut");
            } else if (currentSpeedMbps > bookedDownloadSpeedMin) {
                return t("Üblich");
            } else {
                return t("Schlecht");
            }
        }

        const speedKind = getSpeedKind(currentSpeedMbps, bookedDownloadSpeedAvg, bookedDownloadSpeedMin);

        return (
            <RatingContainerDiv>
                <RatingText>{t("Bewertung")}:&nbsp;</RatingText>
                <PercentageDiv speedKind={speedKind}>
                    {text()}
                </PercentageDiv>
            </RatingContainerDiv>
        );
    }

    return (
        <RootDiv>
            {downloadModemSpeed === null ? (
                <LeftPartDiv>
                    <SpeedContainer>
                        <Speed currentSpeed={downloadSpeed} bookedSpeedMax={bookedDownloadSpeedMax} />
                        <Percentage
                            currentSpeed={downloadSpeed}
                            bookedSpeedMax={bookedDownloadSpeedMax}
                            bookedSpeedAvg={bookedDownloadSpeedAvg}
                            bookedSpeedMin={bookedDownloadSpeedMin} />
                    </SpeedContainer>
                    <SubtitleSpan>
                        {connectionSubtitle()}
                    </SubtitleSpan>
                    {progress(downloadSpeed)}
                </LeftPartDiv>
            ) : (
                <>
                    <LeftPartDiv>
                        <SpeedContainer>
                            <Speed currentSpeed={downloadModemSpeed} bookedSpeedMax={bookedDownloadSpeedMax} />
                            <Percentage
                                currentSpeed={downloadModemSpeed}
                                bookedSpeedMax={bookedDownloadSpeedMax}
                                bookedSpeedAvg={bookedDownloadSpeedAvg}
                                bookedSpeedMin={bookedDownloadSpeedMin} />
                        </SpeedContainer>
                        {routerName === null ? (
                            <SubtitleSpan>
                                {t("Messung an Deinem Anschluss")}
                            </SubtitleSpan>
                        ) : (
                            <SubtitleSpan>
                                {t("Messung an Deinem Anschluss")}: <strong>{routerName}</strong> (Router)
                            </SubtitleSpan>
                        )}
                        {progress(downloadModemSpeed)}
                    </LeftPartDiv>

                    <RightPartDiv>
                        {!R.isNil(onClick) && (
                            <HeaderWithTooltip header={t("Download an Deinem Gerät")}
                                handlePosition="BottomLeft"
                                icon={InfoCircleSvg}
                                size={19}
                                left={-26}
                                bottom={28}>
                                {t("Dein Ergebnis hängt von vielen Faktoren ab. Schau doch mal in Deinen Speedtest Guide. Dort findest Du mehr Infos zu Deinem Ergebnis.")}
                            </HeaderWithTooltip>
                        )}
                        <RightContentDiv>
                            <SubSpeed currentSpeed={downloadSpeed} bookedSpeedMax={bookedDownloadSpeedMax} />
                            <SubtitleRightSpan>
                                {connectionSubtitle()}
                            </SubtitleRightSpan>
                            {rate(downloadSpeed)}
                            {!R.isNil(onClick) && (
                                <>
                                    <RightSpacer />
                                    <NavigationButton onClick={onClick}>
                                        <AboutButtonDiv>
                                            {t("Wie kommt die Bewertung zustande?")}
                                        </AboutButtonDiv>
                                    </NavigationButton>
                                </>
                            )}
                        </RightContentDiv>
                    </RightPartDiv>
                </>
            )}
        </RootDiv>
    );
}

export default DownloadCard;